import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { ChevronLeft, Printer, Wallet, FileText, Receipt, ArrowRight, RefreshCw, AlertTriangle } from "lucide-react";
import { formatDate, formatIDR, clientName } from "../lib/format";
import { hasFinancialAccess } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

export default function ClientStatement() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const finance = hasFinancialAccess(user);

  const load = () => {
    setLoading(true);
    setError(null);
    api.get(`/clients/${id}/statement`)
      .then(({ data }) => { setS(data); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.detail || "Gagal memuat statement"); setLoading(false); });
  };
  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-8 text-sm text-[#8C9096]">Memuat rekap klien...</div>;
  if (error) return (
    <div className="p-8 text-center" data-testid="statement-error">
      <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
      <div className="text-sm font-semibold text-rose-700 mb-1">Tidak dapat menampilkan rekap</div>
      <div className="text-xs text-[#8C9096] mb-3">{error}</div>
      <div className="flex gap-2 justify-center">
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Coba Lagi</Button>
        <Button size="sm" variant="outline" onClick={() => nav("/clients")}>Kembali ke Data Client</Button>
      </div>
    </div>
  );
  if (!s) return null;

  const cname = clientName(s.client);
  const zero = s.total_contract === 0 && s.total_paid === 0;

  return (
    <div className="space-y-6" data-testid="client-statement-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => nav(-1)} className="text-sm text-[#5C6066] hover:text-brand inline-flex items-center gap-1" data-testid="statement-back">
          <ChevronLeft className="w-4 h-4" /> Kembali
        </button>
        <div className="flex flex-wrap gap-2">
          <Link to={`/print/client-statement/${id}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="statement-print"><Printer className="w-3.5 h-3.5 mr-1.5" /> Cetak / PDF</Button>
          </Link>
        </div>
      </div>

      {/* Header */}
      <Card className="p-6 border border-[#E5E0D8] bg-white shadow-none">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">Rekap Klien</div>
            <h1 className="font-display text-2xl font-bold mt-1">{cname}</h1>
            <div className="text-xs text-[#5C6066] mt-1">
              {s.client?.whatsapp && <span>WA {s.client.whatsapp}</span>}
              {s.client?.email && <span> · {s.client.email}</span>}
              {s.client?.address && <span> · {s.client.address}</span>}
            </div>
          </div>
          {finance && (
            <div className="grid grid-cols-3 gap-4 min-w-[280px]" data-testid="statement-totals">
              <div>
                <div className="text-[10px] uppercase text-[#8C9096]">Total Kontrak</div>
                <div className="font-display font-bold text-lg">{formatIDR(s.total_contract)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[#8C9096]">Terbayar</div>
                <div className="font-display font-bold text-lg text-emerald-700">{formatIDR(s.total_paid)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[#8C9096]">Sisa</div>
                <div className="font-display font-bold text-lg text-rose-700">{formatIDR(s.remaining)}</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Bookings */}
      <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-brand" />
            <h3 className="font-display text-sm font-bold text-[#1E2022]">Pemesanan / Booking</h3>
            <span className="text-[11px] text-[#8C9096]">({s.bookings.length})</span>
          </div>
        </div>
        {s.bookings.length === 0 ? (
          <div className="py-6 text-center text-sm text-[#8C9096]">Klien ini belum punya booking.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="statement-bookings-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#8C9096] border-b border-[#EAE3D6]">
                  <th className="py-2 pr-3">Kode</th>
                  <th className="py-2 pr-3">Acara</th>
                  <th className="py-2 pr-3">Tanggal</th>
                  {finance && <th className="py-2 pr-3 text-right">Nilai</th>}
                  {finance && <th className="py-2 pr-3 text-right">Terbayar</th>}
                  {finance && <th className="py-2 pr-3 text-right">Sisa</th>}
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {s.bookings.map((b) => {
                  // find booking id — statement doesn't include booking id, we resolve by code from URL redirect via /bookings?code=
                  return (
                    <tr key={b.booking_code} className="border-b border-[#F5EFE3] hover:bg-[#FAF6EE]">
                      <td className="py-2 pr-3 font-mono text-brand">{b.booking_code}</td>
                      <td className="py-2 pr-3 font-medium">{b.event_name}</td>
                      <td className="py-2 pr-3">{formatDate(b.event_date)}</td>
                      {finance && <td className="py-2 pr-3 text-right font-mono">{formatIDR(b.final_value)}</td>}
                      {finance && <td className="py-2 pr-3 text-right font-mono text-emerald-700">{formatIDR(b.paid)}</td>}
                      {finance && <td className="py-2 pr-3 text-right font-mono text-rose-700">{formatIDR(b.remaining)}</td>}
                      <td className="py-2 pr-3"><StatusBadge status={b.status} /></td>
                      <td className="py-2 pr-3 text-right">
                        <Link to={`/bookings?q=${b.booking_code}`} className="text-brand text-[11px] hover:underline inline-flex items-center gap-0.5">Buka <ArrowRight className="w-3 h-3" /></Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payments */}
      {finance && (
        <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-brand" />
              <h3 className="font-display text-sm font-bold text-[#1E2022]">Riwayat Pembayaran</h3>
              <span className="text-[11px] text-[#8C9096]">({s.payments.length})</span>
            </div>
            <Link to="/finance" className="text-[11px] text-brand hover:underline">Modul Keuangan →</Link>
          </div>
          {s.payments.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#8C9096]">Belum ada pembayaran tercatat.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="statement-payments-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#8C9096] border-b border-[#EAE3D6]">
                    <th className="py-2 pr-3">Tanggal</th>
                    <th className="py-2 pr-3">Booking</th>
                    <th className="py-2 pr-3">Jenis</th>
                    <th className="py-2 pr-3">Metode</th>
                    <th className="py-2 pr-3 text-right">Nominal</th>
                  </tr>
                </thead>
                <tbody>
                  {s.payments.map((p) => (
                    <tr key={p.id} className="border-b border-[#F5EFE3]">
                      <td className="py-2 pr-3">{formatDate(p.payment_date)}</td>
                      <td className="py-2 pr-3 font-mono text-brand">{p.booking_code}</td>
                      <td className="py-2 pr-3">{p.payment_type}</td>
                      <td className="py-2 pr-3">{p.method}</td>
                      <td className="py-2 pr-3 text-right font-mono">{formatIDR(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {!finance && (
        <Card className="p-5 border border-dashed border-[#E5E0D8] bg-[#FAF6EE] text-center text-xs text-[#8C9096]">
          <Receipt className="w-4 h-4 mx-auto mb-1" />
          Data pembayaran hanya bisa dibuka oleh Owner, GM, atau Admin Keuangan.
        </Card>
      )}

      {zero && (
        <div className="text-center text-[11px] text-[#8C9096] italic">
          Belum ada aktivitas finansial untuk klien ini.
        </div>
      )}
    </div>
  );
}
